import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useId } from 'react';
import { IconCalendar, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { Popover } from './Popover.js';
import { cx } from '../utils/cx.js';
import styles from './DatePicker.module.css';
import { Pressable } from './Pressable.js';

export type DatePickerProps = {
  value?: string;
  onChange: (nextIsoDate: string) => void;
  label?: string;
  hint?: string;
  error?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  min?: string;
  max?: string;
  className?: string;
  placeholder?: string;
  style?: CSSProperties;
  ariaLabel?: string;
};

type DayCell = {
  iso: string;
  day: number;
  inMonth: boolean;
  disabled: boolean;
};

const WEEK_DAYS = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'] as const;
const MONTH_LABEL = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });
const MONTH_SHORT_FR = Array.from({ length: 12 }, (_, i) =>
  new Intl.DateTimeFormat('fr-FR', { month: 'short' }).format(new Date(2000, i, 1)),
);

type PanelMode = 'days' | 'months' | 'years';

function monthFullyOutsideRange(year: number, monthIndex: number, min: Date | null, max: Date | null): boolean {
  const lastDay = new Date(year, monthIndex + 1, 0);
  const firstDay = new Date(year, monthIndex, 1);
  if (min && lastDay < min) return true;
  if (max && firstDay > max) return true;
  return false;
}

function yearFullyOutsideRange(year: number, min: Date | null, max: Date | null): boolean {
  const lastDay = new Date(year, 11, 31);
  const firstDay = new Date(year, 0, 1);
  if (min && lastDay < min) return true;
  if (max && firstDay > max) return true;
  return false;
}

function parseIsoDate(value?: string): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map((x) => Number.parseInt(x, 10));
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt;
}

function toIsoDate(dt: Date): string {
  const y = dt.getFullYear();
  const m = `${dt.getMonth() + 1}`.padStart(2, '0');
  const d = `${dt.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatFrDisplay(dt: Date): string {
  const d = `${dt.getDate()}`.padStart(2, '0');
  const m = `${dt.getMonth() + 1}`.padStart(2, '0');
  return `${d}/${m}/${dt.getFullYear()}`;
}

/** Digits only, max JJMMAAAA (8). */
export function frDateDigits(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 8);
}

/** Slot mask: `15` → `15`, `1503` → `15/03`, `15032026` → `15/03/2026`. */
export function formatFrDateMask(digitsOrRaw: string): string {
  const d = frDateDigits(digitsOrRaw);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/** Caret index in masked string after `digitIndex` digits (0 = start). */
export function frDateMaskCaretPos(masked: string, digitIndex: number): number {
  if (digitIndex <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < masked.length; i += 1) {
    if (/\d/.test(masked[i]!)) {
      seen += 1;
      if (seen === digitIndex) return i + 1;
    }
  }
  return masked.length;
}

/** Parse typed FR date (JJ/MM/AAAA, JJ-MM-AAAA, digit run, …) → ISO YYYY-MM-DD or null. */
export function parseTypedFrDate(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return parseIsoDate(v) ? v : null;
  }
  const digits = frDateDigits(v);
  if (digits.length === 8) {
    const day = Number.parseInt(digits.slice(0, 2), 10);
    const month = Number.parseInt(digits.slice(2, 4), 10);
    const year = Number.parseInt(digits.slice(4, 8), 10);
    const dt = new Date(year, month - 1, day);
    if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
      return null;
    }
    return toIsoDate(dt);
  }
  const m = v.match(/^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{4})$/);
  if (!m) return null;
  const day = Number.parseInt(m[1]!, 10);
  const month = Number.parseInt(m[2]!, 10);
  const year = Number.parseInt(m[3]!, 10);
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return null;
  return toIsoDate(dt);
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function monthStart(dt: Date): Date {
  return new Date(dt.getFullYear(), dt.getMonth(), 1);
}

function addMonths(dt: Date, count: number): Date {
  return new Date(dt.getFullYear(), dt.getMonth() + count, 1);
}

function startGrid(dt: Date): Date {
  const first = monthStart(dt);
  const jsDay = first.getDay(); // 0 Sunday
  const mondayIndex = (jsDay + 6) % 7; // Monday=0
  return new Date(first.getFullYear(), first.getMonth(), first.getDate() - mondayIndex);
}

function buildCells(viewMonth: Date, min?: Date | null, max?: Date | null): DayCell[] {
  const out: DayCell[] = [];
  const start = startGrid(viewMonth);
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const disabled = (min && d < min) || (max && d > max) || false;
    out.push({
      iso: toIsoDate(d),
      day: d.getDate(),
      inMonth: d.getMonth() === viewMonth.getMonth(),
      disabled,
    });
  }
  return out;
}

export function DatePicker({
  value,
  onChange,
  label,
  hint,
  error,
  id,
  name,
  disabled,
  required,
  min,
  max,
  className,
  placeholder = 'JJ/MM/AAAA',
  style,
  ariaLabel,
}: DatePickerProps) {
  const autoId = useId();
  const triggerId = id ?? name ?? autoId;
  const hiddenId = `${triggerId}-value`;
  const errorId = `${triggerId}-error`;
  const parsedValue = useMemo(() => parseIsoDate(value), [value]);
  const minDate = useMemo(() => parseIsoDate(min), [min]);
  const maxDate = useMemo(() => parseIsoDate(max), [max]);
  const today = useMemo(() => new Date(), []);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<PanelMode>('days');
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    monthStart(parsedValue ?? minDate ?? today),
  );
  /** First year shown in the year grid (12-year block). */
  const [yearBlockStart, setYearBlockStart] = useState(() => {
    const y = (parsedValue ?? minDate ?? today).getFullYear();
    return Math.floor(y / 12) * 12;
  });
  const [text, setText] = useState(() => (parsedValue ? formatFrDisplay(parsedValue) : ''));
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textSyncedFromValue = useRef(value);
  /** Latest mask string — blur/Tab must not read stale `text` state. */
  const textRef = useRef(text);
  /** Digit count before caret after next mask paint; null = leave caret alone. */
  const caretDigitsRef = useRef<number | null>(null);

  const setMaskedText = (next: string) => {
    textRef.current = next;
    setText(next);
  };

  useEffect(() => {
    if (textSyncedFromValue.current === value) return;
    textSyncedFromValue.current = value;
    setMaskedText(parsedValue ? formatFrDisplay(parsedValue) : '');
  }, [value, parsedValue]);

  useLayoutEffect(() => {
    const digitIndex = caretDigitsRef.current;
    if (digitIndex == null) return;
    caretDigitsRef.current = null;
    const el = inputRef.current;
    if (!el) return;
    const pos = frDateMaskCaretPos(text, digitIndex);
    el.setSelectionRange(pos, pos);
  }, [text]);

  /** Drop stale committed ISO when draft is complete but unusable — keep typed mask. */
  const clearCommittedValue = () => {
    if (!value) return;
    textSyncedFromValue.current = '';
    onChange('');
  };

  const applyMaskedDigits = (digits: string, caretDigitIndex: number) => {
    const masked = formatFrDateMask(digits);
    caretDigitsRef.current = caretDigitIndex;
    setMaskedText(masked);
    if (digits.length !== 8) return;
    const iso = parseTypedFrDate(masked);
    if (!iso) {
      // Impossible calendar date (e.g. 32/01/2026) — drop stale commit.
      clearCommittedValue();
      return;
    }
    // Commit parseable ISO even outside min/max — parent validates (e.g. expiry future).
    textSyncedFromValue.current = iso;
    if (iso !== value) onChange(iso);
  };

  const commitTypedDate = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      clearCommittedValue();
      setMaskedText('');
      return;
    }
    const masked = formatFrDateMask(trimmed);
    const iso = parseTypedFrDate(masked);
    if (!iso) {
      // Incomplete or impossible — keep draft text, clear stale ISO.
      setMaskedText(masked);
      clearCommittedValue();
      return;
    }
    textSyncedFromValue.current = iso;
    setMaskedText(formatFrDisplay(parseIsoDate(iso)!));
    if (iso !== value) onChange(iso);
  };

  /** Stable refs — bare `parseIsoDate` returns new Date() each render and would retrigger open-sync effect → month nav snaps back. */
  useEffect(() => {
    if (!open) return;
    const draftIso = parseTypedFrDate(textRef.current);
    const draftDate = draftIso ? parseIsoDate(draftIso) : null;
    const anchor = draftDate ?? parsedValue ?? minDate ?? today;
    setViewMonth(monthStart(anchor));
    setPanel('days');
    setYearBlockStart(Math.floor(anchor.getFullYear() / 12) * 12);
    const t = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLButtonElement>('button[class*="day"]:not(:disabled)');
      first?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, parsedValue, minDate, today]);

  const cells = useMemo(() => buildCells(viewMonth, minDate, maxDate), [viewMonth, minDate, maxDate]);

  return (
    <div className={cx(styles.wrap, className)}>
      {label ? (
        <label className={styles.label} htmlFor={triggerId}>
          {label}
        </label>
      ) : null}

      <input type="hidden" id={hiddenId} name={name} value={value ?? ''} required={required} />

      <Popover
        opened={open}
        onChange={setOpen}
        portal
        modal
        align="start"
        portalMinWidth={280}
        toggleOnTargetClick={false}
        target={
          <div
            className={cx(
              styles.trigger,
              error && styles.error,
              disabled && styles.disabled,
            )}
            style={style}
          >
            <input
              ref={inputRef}
              id={triggerId}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
              className={styles.triggerInput}
              placeholder={placeholder}
              value={text}
              disabled={disabled}
              aria-label={ariaLabel ?? label}
              aria-invalid={!!error}
              aria-required={required || undefined}
              aria-describedby={error ? errorId : undefined}
              aria-haspopup="dialog"
              aria-expanded={open}
              onChange={(e) => {
                const el = e.currentTarget;
                const sel = el.selectionStart ?? el.value.length;
                const digitsBefore = frDateDigits(el.value.slice(0, sel)).length;
                const digits = frDateDigits(el.value);
                applyMaskedDigits(digits, Math.min(digitsBefore, digits.length));
              }}
              onBlur={() => commitTypedDate(textRef.current)}
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  const el = e.currentTarget;
                  const start = el.selectionStart ?? 0;
                  const end = el.selectionEnd ?? 0;
                  const cur = textRef.current;
                  if (start === end && start > 0 && cur[start - 1] === '/') {
                    e.preventDefault();
                    const digits = frDateDigits(cur);
                    const dBefore = frDateDigits(cur.slice(0, start)).length;
                    const next = digits.slice(0, Math.max(0, dBefore - 1)) + digits.slice(dBefore);
                    applyMaskedDigits(next, Math.max(0, dBefore - 1));
                    return;
                  }
                }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitTypedDate(textRef.current);
                  (e.currentTarget as HTMLInputElement).blur();
                }
                if (e.key === 'ArrowDown' && !open) {
                  e.preventDefault();
                  setOpen(true);
                }
              }}
            />
            <Pressable
              type="button"
              className={styles.calendarBtn}
              aria-label="Ouvrir le calendrier"
              disabled={disabled}
              tabIndex={-1}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen((prev) => !prev);
              }}
            >
              <IconCalendar size={16} stroke={1.8} className={styles.calendarIcon} aria-hidden />
            </Pressable>
          </div>
        }
      >
        <div ref={panelRef} className={styles.panel} role="dialog" aria-label="Calendrier">
          {panel === 'days' ? (
            <>
              <div className={styles.header}>
                <Pressable
                  type="button"
                  className={styles.navBtn}
                  aria-label="Mois précédent"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewMonth((m) => addMonths(m, -1));
                  }}
                >
                  <IconChevronLeft size={16} stroke={1.8} />
                </Pressable>
                <Pressable
                  type="button"
                  className={styles.monthLabelBtn}
                  aria-label="Choisir mois et année"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setYearBlockStart(Math.floor(viewMonth.getFullYear() / 12) * 12);
                    setPanel('months');
                  }}
                >
                  {MONTH_LABEL.format(viewMonth)}
                </Pressable>
                <Pressable
                  type="button"
                  className={styles.navBtn}
                  aria-label="Mois suivant"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewMonth((m) => addMonths(m, 1));
                  }}
                >
                  <IconChevronRight size={16} stroke={1.8} />
                </Pressable>
              </div>

              <div className={styles.weekdays}>
                {WEEK_DAYS.map((d) => (
                  <span key={d} className={styles.weekday}>
                    {d}
                  </span>
                ))}
              </div>

              <div className={styles.grid}>
                {cells.map((c) => {
                  const dt = parseIsoDate(c.iso);
                  const isSelected = dt && parsedValue ? sameDay(dt, parsedValue) : false;
                  const isToday = dt ? sameDay(dt, today) : false;
                  return (
                    <Pressable
                      key={c.iso}
                      type="button"
                      className={cx(
                        styles.day,
                        !c.inMonth && styles.outside,
                        isSelected && styles.selected,
                        isToday && !isSelected && styles.today,
                      )}
                      disabled={c.disabled}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        textSyncedFromValue.current = c.iso;
                        setText(formatFrDisplay(parseIsoDate(c.iso)!));
                        onChange(c.iso);
                        setOpen(false);
                      }}
                    >
                      {c.day}
                    </Pressable>
                  );
                })}
              </div>
            </>
          ) : null}

          {panel === 'months' ? (
            <>
              <div className={styles.header}>
                <Pressable
                  type="button"
                  className={styles.navBtn}
                  aria-label="Année précédente"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewMonth((m) => new Date(m.getFullYear() - 1, m.getMonth(), 1));
                  }}
                >
                  <IconChevronLeft size={16} stroke={1.8} />
                </Pressable>
                <Pressable
                  type="button"
                  className={styles.monthLabelBtn}
                  aria-label="Choisir une année"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setYearBlockStart(Math.floor(viewMonth.getFullYear() / 12) * 12);
                    setPanel('years');
                  }}
                >
                  {viewMonth.getFullYear()}
                </Pressable>
                <Pressable
                  type="button"
                  className={styles.navBtn}
                  aria-label="Année suivante"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewMonth((m) => new Date(m.getFullYear() + 1, m.getMonth(), 1));
                  }}
                >
                  <IconChevronRight size={16} stroke={1.8} />
                </Pressable>
              </div>
              <div className={styles.monthYearGrid}>
                {MONTH_SHORT_FR.map((label, monthIndex) => {
                  const y = viewMonth.getFullYear();
                  const blocked = monthFullyOutsideRange(y, monthIndex, minDate, maxDate);
                  return (
                    <Pressable
                      key={monthIndex}
                      type="button"
                      className={cx(
                        styles.monthYearCell,
                        viewMonth.getMonth() === monthIndex && styles.monthYearCellActive,
                      )}
                      disabled={blocked}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setViewMonth(new Date(y, monthIndex, 1));
                        setPanel('days');
                      }}
                    >
                      {label}
                    </Pressable>
                  );
                })}
              </div>
            </>
          ) : null}

          {panel === 'years' ? (
            <>
              <div className={styles.header}>
                <Pressable
                  type="button"
                  className={styles.navBtn}
                  aria-label="Bloc d’années précédent"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setYearBlockStart((s) => s - 12);
                  }}
                >
                  <IconChevronLeft size={16} stroke={1.8} />
                </Pressable>
                <Pressable
                  type="button"
                  className={styles.monthLabelBtn}
                  aria-label="Retour au choix du mois"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPanel('months');
                  }}
                >
                  {yearBlockStart} — {yearBlockStart + 11}
                </Pressable>
                <Pressable
                  type="button"
                  className={styles.navBtn}
                  aria-label="Bloc d’années suivant"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setYearBlockStart((s) => s + 12);
                  }}
                >
                  <IconChevronRight size={16} stroke={1.8} />
                </Pressable>
              </div>
              <div className={styles.monthYearGrid}>
                {Array.from({ length: 12 }, (_, i) => yearBlockStart + i).map((year) => {
                  const blocked = yearFullyOutsideRange(year, minDate, maxDate);
                  return (
                    <Pressable
                      key={year}
                      type="button"
                      className={cx(
                        styles.monthYearCell,
                        viewMonth.getFullYear() === year && styles.monthYearCellActive,
                      )}
                      disabled={blocked}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setViewMonth(new Date(year, viewMonth.getMonth(), 1));
                        setPanel('months');
                      }}
                    >
                      {year}
                    </Pressable>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      </Popover>

      {error ? (
        <span id={errorId} className={styles.errText} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
}

